import { LoadingIndicator } from '../common/components/loading-indicator';
import { FederatedSearchUpdateToEvent } from '../data-exchange/update-to-event/federated-search-update-to-event';
import { getRealUrlPrefix } from '~/session';
import { RefObject, Suspense, useCallback, useMemo } from 'react';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { useEventFederatedQueryIds } from '../event-panel/use-event-federated-query-ids';
import { ApiFederatedQueriesRequest } from 'mark43-federated-search/dist/lib/generated/api-types/models-federated-query-ApiFederatedQueriesRequest';
import { EventAutocompleteComponent } from '../data-exchange/components/event-autocomplete';
import { RenderButtonsParams } from 'mark43-federated-search/dist/lib/components/FederatedQueryResults/types';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { FederatedSearchQueryResults } from './federated-search-query-results';
import { FederatedSearchParsedResult } from 'mark43-federated-search/dist/lib/types';
import { isRmsDmvOrNletsResult } from '../data-exchange/utils';
import {
    useUpdateToEventBulkBootstrap,
    BulkBootstrapResult,
} from '../data-exchange/update-to-event/use-update-to-event-bulk-bootstrap';
import { FailedToLoadError } from '../common/components/failed-to-load-error';
import { useIsFederatedSearchEnabledButConfirmedUnavailable } from './federated-search-remote-state-context';

function UpdateToEventWrapper({
    queryId,
    queryResultId,
    linkedEventId,
    federatedSearchParsedResult,
    isEligibleForResultActions,
    bulkBootstrapResult,
}: {
    queryId: string;
    queryResultId: string;
    linkedEventId?: number;
    federatedSearchParsedResult?: FederatedSearchParsedResult;
    isEligibleForResultActions: boolean;
    bulkBootstrapResult: BulkBootstrapResult;
}) {
    const { isFirstResponder } = useApplicationType();
    const unitAssignment = useUnitAssignment();
    const unitAssignedEventId = unitAssignment?.assignedEvent?.eventId;
    return (
        <FederatedSearchUpdateToEvent
            key={queryResultId}
            queryId={queryId}
            queryResultId={queryResultId}
            linkedEventId={linkedEventId}
            federatedSearchParsedResult={federatedSearchParsedResult}
            isEligibleForResultActions={isEligibleForResultActions}
            bulkBootstrapResult={bulkBootstrapResult}
            unitAssignedEventId={isFirstResponder ? unitAssignedEventId || null : undefined}
            EventAutocompleteComponent={isFirstResponder ? undefined : EventAutocompleteComponent}
            showTrailingVisualIcon
        />
    );
}

export function FederatedSearchEventPanelDexActivityList({
    eventId,
    exportButtonContainerRef,
}: {
    eventId: number | undefined;
    exportButtonContainerRef?: RefObject<HTMLElement>;
}) {
    const isFederatedSearchEnabledButConfirmedUnavailable = useIsFederatedSearchEnabledButConfirmedUnavailable();
    const urlPrefix = getRealUrlPrefix();
    const federatedQueryIds = useEventFederatedQueryIds({ eventId });

    const { registerIdentifier, result: bulkBootstrapResult } = useUpdateToEventBulkBootstrap({
        eventId,
    });

    const renderButtons = useCallback(
        (data: RenderButtonsParams) => {
            const queryId = data.activeQueryResult?.queryId;
            const queryResultId = data.activeQueryResult?.id;
            const federatedSearchParsedResult = data.parsedResultObject;
            const { isEligibleForResultActions } = data;
            if (
                typeof queryId === 'undefined' ||
                typeof queryResultId === 'undefined' ||
                !isRmsDmvOrNletsResult(federatedSearchParsedResult, isEligibleForResultActions)
            ) {
                return undefined;
            }

            registerIdentifier({ queryId, queryResultId });

            return (
                <UpdateToEventWrapper
                    queryId={queryId}
                    queryResultId={queryResultId}
                    linkedEventId={eventId}
                    federatedSearchParsedResult={federatedSearchParsedResult}
                    isEligibleForResultActions={isEligibleForResultActions}
                    bulkBootstrapResult={bulkBootstrapResult}
                />
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [eventId, bulkBootstrapResult, registerIdentifier]
    );

    const downloadFiles = useCallback(
        (fileList: { name: string; url: string }[]) => {
            if (!electronBridge || fileList.length === 0) {
                return;
            }

            const firstFile = fileList[0];

            if (fileList.length === 1 && firstFile) {
                electronBridge.downloadFile({
                    url: `${urlPrefix}${firstFile.url}`,
                    fileName: firstFile.name,
                });
            } else {
                electronBridge.zipAndDownloadFiles(
                    fileList.map((file, idx) => ({
                        url: `${urlPrefix}${file.url}`,
                        fileName: `${idx}_${file.name}`,
                    }))
                );
            }
        },
        [urlPrefix]
    );

    const filters: ApiFederatedQueriesRequest = useMemo(() => {
        return {
            federatedQueryIds,
            pageable: {},
        };
    }, [federatedQueryIds]);

    if (isFederatedSearchEnabledButConfirmedUnavailable) {
        return <FailedToLoadError noun="Universal Search results" />;
    }

    return (
        <Suspense fallback={<LoadingIndicator />}>
            <FederatedSearchQueryResults
                filters={filters}
                renderButtons={renderButtons}
                exportButtonContainerRef={exportButtonContainerRef}
                eventNumber={eventId}
                downloadFiles={electronBridge && downloadFiles}
            />
        </Suspense>
    );
}
